// /admin/configuracion-ia
// Editor multi-tab del contexto IA que usa Limpack:
//   1. Reportes — ai_business_context (Claude analiza reportes)
//   2. Marca · Gerente — brand_context_marketing (Gerente de Marketing toma decisiones)
//   3. Marca · Copywriter — copywriter_voice_voseo (escribe captions/slides en voseo)
//   4. Marca · Diseñador — brand_palette_v3 (paleta + plantillas + tipografía, JSON)
//   5. Meta marketing — agente_marketing_meta (handles, hashtags, kits, datos verificados, JSON)
// Todo persiste en app_config. Edge functions leen de acá en runtime — cambios SIN deploy.

import { useEffect, useMemo, useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

const TABS = [
  {
    key: 'ai_business_context',
    label: 'Reportes',
    icon: '📊',
    descripcion: 'Contexto que Claude usa al analizar reportes del admin. Le explica en qué etapa está Limpack y qué tiene montado.',
    formato: 'texto',
    placeholder: 'Describí tu negocio: modelo, etapa, qué tenés ya montado, qué falta, fundadores, etc.',
  },
  {
    key: 'brand_context_marketing',
    label: 'Marca · Gerente',
    icon: '🎩',
    descripcion: 'Lo que el Gerente de Marketing usa para decidir qué se comunica, cuándo, en qué pilar, a qué audiencia. Es el "cerebro estratégico" del agente.',
    formato: 'markdown',
    placeholder: 'Identidad, pilares editoriales, audiencias, datos verificados, mensajes clave, reglas anti-error.',
  },
  {
    key: 'social_media_manager_context',
    label: 'Marca · Social Media Mgr',
    icon: '📱',
    descripcion: 'Lo que el Social Media Manager usa para decidir formato, canal, cadencia y horario óptimo. Orquesta Copywriter + Diseñador para cada pieza.',
    formato: 'markdown',
    placeholder: 'Formatos IG, horarios óptimos, series, adaptación multi-plataforma, mecánicas nativas.',
  },
  {
    key: 'copywriter_voice_voseo',
    label: 'Marca · Copywriter',
    icon: '✍️',
    descripcion: 'Lo que el Copywriter usa para escribir captions y slides. Voz, tono, palabras SÍ/NO, principios de copy, do/don\'ts.',
    formato: 'markdown',
    placeholder: 'Voseo, anti-corporativo, palabras prohibidas, formato JSON esperado.',
  },
  {
    key: 'disenador_context',
    label: 'Marca · Diseñador',
    icon: '🎨',
    descripcion: 'Rol del Diseñador como agente. Recibe slides del Copywriter, decide engine de imagen, plantilla y composición. Fase 1 solo Ideogram, fase 2 suma Flux y SVG.',
    formato: 'markdown',
    placeholder: 'Engines, plantillas, principios de composición, workflow, errores prohibidos.',
  },
  {
    key: 'brand_palette_v3',
    label: 'Marca · Paleta (JSON)',
    icon: '🎨',
    descripcion: 'Data visual exacta que consulta el Diseñador con tool consultar_paleta(): primarios, tipografía Inter, plantillas feed/story con specs detalladas.',
    formato: 'json',
    placeholder: '{ "primarios": {...}, "tipografia": {...}, "plantillas_feed_1080x1080": {...} }',
  },
  {
    key: 'agente_marketing_meta',
    label: 'Meta marketing',
    icon: '📌',
    descripcion: 'Datos cross-agente: handles oficiales, hashtags, taglines, pilares, horarios sugeridos, datos verificados de encuesta, catálogo de kits.',
    formato: 'json',
    placeholder: '{ "handles": {...}, "hashtags_b2c": [...], "datos_verificados_encuesta_n125": {...} }',
  },
]

const COSTO_MTOK_IN = 3
const COSTO_MTOK_OUT = 15

export default function ConfiguracionIA() {
  const [activeTab, setActiveTab] = useState('ai_business_context')
  const [valores, setValores] = useState({})       // { key: valor actual del textarea }
  const [originales, setOriginales] = useState({}) // { key: valor que vino de DB }
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)
  const [error, setError] = useState(null)
  const [jsonError, setJsonError] = useState(null) // error parseo JSON del tab activo

  useEffect(() => { cargarTodo() }, [])

  async function cargarTodo() {
    setLoading(true)
    try {
      const keys = TABS.map(t => t.key)
      const { data, error } = await supabase
        .from('app_config').select('key, value').in('key', keys)
      if (error) throw error
      const map = {}
      ;(data || []).forEach(r => { map[r.key] = r.value || '' })
      // si falta alguna key, queda string vacío
      keys.forEach(k => { if (!(k in map)) map[k] = '' })
      setValores(map)
      setOriginales(map)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  const tabActual = useMemo(() => TABS.find(t => t.key === activeTab) || TABS[0], [activeTab])

  function setValorTab(v) {
    setValores(prev => ({ ...prev, [activeTab]: v }))
    setMsg(null)
    if (tabActual.formato === 'json') {
      try { JSON.parse(v); setJsonError(null) }
      catch (e) { setJsonError(e.message) }
    } else {
      setJsonError(null)
    }
  }

  const valorActual = valores[activeTab] || ''
  const valorOriginal = originales[activeTab] || ''
  const cambios = valorActual !== valorOriginal
  const chars = valorActual.length
  const tokens = Math.ceil(chars / 4)
  const costoInputUSD = ((tokens / 1_000_000) * COSTO_MTOK_IN).toFixed(4)

  async function guardar() {
    if (tabActual.formato === 'json' && jsonError) {
      setError('No se puede guardar: el JSON tiene errores de sintaxis. Corregí antes.')
      return
    }
    setSaving(true); setMsg(null); setError(null)
    try {
      const { error } = await supabase
        .from('app_config')
        .upsert({ key: activeTab, value: valorActual }, { onConflict: 'key' })
      if (error) throw error
      setOriginales(prev => ({ ...prev, [activeTab]: valorActual }))
      setMsg('✓ Guardado. Las edge functions van a usar este contexto en la próxima llamada.')
      setTimeout(() => setMsg(null), 5000)
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  function descartar() {
    setValores(prev => ({ ...prev, [activeTab]: valorOriginal }))
    setError(null); setMsg(null); setJsonError(null)
  }

  function formatearJSON() {
    if (tabActual.formato !== 'json') return
    try {
      const parsed = JSON.parse(valorActual)
      setValorTab(JSON.stringify(parsed, null, 2))
      setMsg('JSON formateado (no guardado todavía)')
      setTimeout(() => setMsg(null), 3000)
    } catch (e) {
      setError('No se pudo formatear: ' + e.message)
    }
  }

  return (
    <AdminLayout title="🤖 Configuración IA · Multi-contexto">
      <div className="page">
        <p className="muted" style={{ marginBottom: 18 }}>
          Cada agente IA de Limpack lee su contexto desde acá en runtime. Cambiá un tab, guardá, y la próxima llamada usa lo nuevo — sin redeploy.
        </p>

        {/* Tabs */}
        <div className="ci-tabs">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => { setActiveTab(t.key); setError(null); setMsg(null); setJsonError(null) }}
              className={`ci-tab ${activeTab === t.key ? 'active' : ''} ${valores[t.key] !== originales[t.key] ? 'cambios' : ''}`}
            >
              <span className="ci-tab-icon">{t.icon}</span>
              <span>{t.label}</span>
              {valores[t.key] !== originales[t.key] && <span className="ci-tab-dot" title="Cambios sin guardar" />}
            </button>
          ))}
        </div>

        <div className="ci-grid">
          <div className="ci-main">
            <div className="ci-head">
              <div>
                <strong>{tabActual.icon} {tabActual.label}</strong>
                <div className="ci-descripcion">{tabActual.descripcion}</div>
                <div className="ci-stats">
                  <span><code>app_config.{tabActual.key}</code></span>
                  <span>{chars.toLocaleString('es-UY')} chars · ~{tokens.toLocaleString('es-UY')} tokens · costo input ≈ <strong>${costoInputUSD} USD</strong> por llamada</span>
                </div>
              </div>
              {tabActual.formato === 'json' && (
                <button className="ci-btn-link" onClick={formatearJSON} disabled={!!jsonError}>
                  ⛀ Formatear JSON
                </button>
              )}
            </div>

            {loading && <div className="ci-loading">Cargando configuración…</div>}

            {!loading && (
              <textarea
                className={`ci-textarea ${jsonError ? 'json-error' : ''} ${tabActual.formato === 'json' ? 'mono' : ''}`}
                value={valorActual}
                onChange={e => setValorTab(e.target.value)}
                placeholder={tabActual.placeholder}
                rows={28}
                spellCheck={false}
              />
            )}

            {jsonError && (
              <div className="ci-warn">
                <strong>⚠ JSON inválido:</strong> {jsonError}
              </div>
            )}

            {error && <div className="ci-error"><strong>Error:</strong> {error}</div>}
            {msg && <div className="ci-success">{msg}</div>}

            <div className="ci-actions">
              <button
                className="ci-btn-primary"
                onClick={guardar}
                disabled={saving || !cambios || loading || !!jsonError}
              >
                {saving ? 'Guardando…' : cambios ? `Guardar ${tabActual.label.toLowerCase()}` : 'Sin cambios'}
              </button>
              {cambios && (
                <button className="ci-btn-secondary" onClick={descartar} disabled={saving}>
                  Descartar cambios
                </button>
              )}
            </div>
          </div>

          <aside className="ci-side">
            <TabHelp tab={tabActual} />

            <div className="ci-card">
              <h3>💰 Costo</h3>
              <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>
                Sonnet 4.6 a USD 3/MTok input + USD 15/MTok output.<br />
                Este contexto se paga en cada llamada que lo lea (Gerente, Copywriter, Diseñador).
              </p>
            </div>

            <div className="ci-card">
              <h3>🔄 Qué agente lee qué</h3>
              <ul>
                <li><strong>ai_business_context</strong> → análisis de reportes en /admin/reportes</li>
                <li><strong>brand_context_marketing</strong> → Gerente (decide qué/audiencia)</li>
                <li><strong>social_media_manager_context</strong> → SMM (decide formato/canal/cadencia)</li>
                <li><strong>copywriter_voice_voseo</strong> → Copywriter (escribe texto)</li>
                <li><strong>disenador_context</strong> → Diseñador (rol, decide engine)</li>
                <li><strong>brand_palette_v3</strong> → Diseñador (data visual JSON)</li>
                <li><strong>agente_marketing_meta</strong> → todos (handles, hashtags, datos)</li>
              </ul>
            </div>
          </aside>
        </div>
      </div>

      <Estilos />
    </AdminLayout>
  )
}

function TabHelp({ tab }) {
  const ayudas = {
    ai_business_context: [
      { titulo: 'Etapa', body: 'pre-launch, launching, growth, mature' },
      { titulo: 'Modelo', body: 'qué vendés, cómo cobrás, frecuencia' },
      { titulo: 'Lo que YA tenés', body: 'integraciones técnicas que NO hay que recomendar' },
      { titulo: 'Lo que falta', body: 'bloqueos actuales, próximos pasos' },
      { titulo: 'Instrucciones', body: '"NO recomiendes X", "SÍ enfocate en Y"' },
    ],
    brand_context_marketing: [
      { titulo: 'Identidad', body: 'nombre, tagline oficial, misión' },
      { titulo: 'Pilares editoriales', body: 'costo_invisible, lanzamiento, retención, etc.' },
      { titulo: 'Audiencias', body: 'B2C hogares, B2B empresas/administradoras, retención' },
      { titulo: 'Datos verificados', body: 'encuesta n=125, NO inventar números' },
      { titulo: 'Reglas anti-error', body: 'marcas, IVA, regalo sorpresa, B2B' },
      { titulo: 'Workflow', body: 'cómo delega al Copywriter y Diseñador' },
    ],
    copywriter_voice_voseo: [
      { titulo: 'Voseo rioplatense', body: 'vos, podés, sumate, contanos' },
      { titulo: 'Palabras SÍ', body: 'kit, suscripción, barrio, a tu puerta...' },
      { titulo: 'Palabras NO', body: 'premium, integral, sinergia, leads...' },
      { titulo: 'Principios', body: 'Hablá en plata, del problema, usá el nombre' },
      { titulo: 'JSON output', body: 'estructura exacta de caption + slides' },
    ],
    social_media_manager_context: [
      { titulo: 'Decide formato + canal', body: 'carrusel, post, story; IG fase 1' },
      { titulo: 'Decide cadencia', body: '1 pieza vs serie escalonada (2-4 piezas)' },
      { titulo: 'Horarios UYT', body: 'L-V 19:30 feed · 13:00 story · Sáb 11:00' },
      { titulo: 'Orquesta', body: 'llama a Copywriter y Diseñador, NO escribe ni diseña' },
      { titulo: 'Reglas duras', body: 'última slide = cierre_cta, 1 idea por slide' },
    ],
    disenador_context: [
      { titulo: 'Engines fase 1', body: 'Solo Ideogram (Replicate)' },
      { titulo: 'Engines fase 2', body: 'Suma SVG server-side + Flux Pro' },
      { titulo: 'Decide', body: 'engine + plantilla + paleta + prompt' },
      { titulo: 'NO decide', body: 'texto, formato (SMM), publicación' },
      { titulo: 'Rechaza pieza', body: 'si tiene marca proveedor o palabra prohibida' },
    ],
    brand_palette_v3: [
      { titulo: 'Primarios', body: 'Navy #1A3461 + Teal #00A8B5' },
      { titulo: 'Soporte', body: 'Sky #5BBDD8 + Paper #F1F5F9' },
      { titulo: 'Tipografía Inter', body: 'Display 900 / H1 800 / H2 700 / Body 500' },
      { titulo: 'Plantillas feed', body: 'promo, beneficio, testimonio, novedad' },
      { titulo: 'Plantillas story', body: 'novedad, pregunta, behind, promo' },
    ],
    agente_marketing_meta: [
      { titulo: 'Handles oficiales', body: '@limpackuy, limpack.uy, hola@limpack.uy' },
      { titulo: 'Hashtags', body: 'B2C oficiales + B2B vertical empresas' },
      { titulo: 'Horarios sugeridos', body: 'L-V 19:30, Sáb 11:00, lanzamientos 09:00' },
      { titulo: 'Catálogo', body: '8 kits en 3 líneas + 21 productos' },
      { titulo: 'Encuesta', body: 'n=125 datos reales hogares Montevideo' },
    ],
  }
  const items = ayudas[tab.key] || []
  return (
    <div className="ci-card">
      <h3>💡 Qué va acá</h3>
      <ul>
        {items.map((i, idx) => (
          <li key={idx}><strong>{i.titulo}:</strong> {i.body}</li>
        ))}
      </ul>
    </div>
  )
}

function Estilos() {
  return (
    <style>{`
      /* Tabs */
      .ci-tabs {
        display: flex; gap: 4px; margin-bottom: 14px;
        border-bottom: 1px solid #e5e7eb;
        overflow-x: auto; padding-bottom: 0;
      }
      .ci-tab {
        position: relative;
        background: none; border: 0;
        padding: 10px 14px;
        font-size: 13px; font-weight: 600;
        color: #64748b; cursor: pointer;
        border-bottom: 2px solid transparent;
        white-space: nowrap;
        display: inline-flex; align-items: center; gap: 6px;
        font-family: inherit;
        transition: color .12s, border-color .12s;
      }
      .ci-tab:hover { color: #1a3461; }
      .ci-tab.active { color: #1a3461; border-bottom-color: #00A8B5; font-weight: 700; }
      .ci-tab-icon { font-size: 14px; }
      .ci-tab.cambios { color: #ea580c; }
      .ci-tab.cambios.active { color: #1a3461; border-bottom-color: #ea580c; }
      .ci-tab-dot {
        width: 6px; height: 6px; border-radius: 50%;
        background: #ea580c; margin-left: 4px;
      }

      /* Grid */
      .ci-grid { display: grid; grid-template-columns: 1fr 320px; gap: 20px; align-items: flex-start; }
      @media (max-width: 900px) { .ci-grid { grid-template-columns: 1fr; } }
      .ci-main { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 18px; }
      .ci-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 10px; margin-bottom: 14px; flex-wrap: wrap; }
      .ci-head > div:first-child { flex: 1; min-width: 0; }
      .ci-descripcion { font-size: 12px; color: #475569; margin-top: 4px; line-height: 1.5; }
      .ci-stats { font-size: 11px; color: #94a3b8; margin-top: 6px; display: flex; gap: 12px; flex-wrap: wrap; }
      .ci-stats code { background: #f1f5f9; padding: 1px 6px; border-radius: 4px; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; color: #0f172a; }

      /* Textarea */
      .ci-textarea {
        width: 100%; box-sizing: border-box;
        font-family: ui-sans-serif, system-ui, sans-serif;
        font-size: 13px; line-height: 1.6;
        padding: 14px;
        border: 1px solid #d1d5db; border-radius: 8px;
        background: #fafbfc;
        resize: vertical;
        min-height: 480px;
      }
      .ci-textarea.mono {
        font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
        font-size: 12.5px;
        background: #f8fafc;
      }
      .ci-textarea:focus { outline: none; border-color: #1a3461; box-shadow: 0 0 0 3px rgba(26,52,97,.1); }
      .ci-textarea.json-error { border-color: #f59e0b; background: #fffbeb; }

      /* Botones */
      .ci-actions { display: flex; gap: 8px; margin-top: 14px; }
      .ci-btn-primary {
        padding: 10px 20px; background: #1a3461; color: #fff;
        border: 0; border-radius: 8px; font-weight: 700; font-size: 14px;
        cursor: pointer; font-family: inherit;
      }
      .ci-btn-primary:disabled { opacity: .45; cursor: not-allowed; }
      .ci-btn-secondary {
        padding: 10px 18px; background: #fff; color: #475569;
        border: 1px solid #d1d5db; border-radius: 8px; font-weight: 600; font-size: 14px;
        cursor: pointer; font-family: inherit;
      }
      .ci-btn-link {
        background: none; border: 0; color: #1a3461; font-size: 12px;
        cursor: pointer; text-decoration: underline; padding: 4px 8px;
      }
      .ci-btn-link:disabled { opacity: .4; cursor: not-allowed; }

      /* Mensajes */
      .ci-loading { padding: 40px; text-align: center; color: #6b7280; }
      .ci-error { margin-top: 10px; padding: 10px 12px; background: #fee2e2; border: 1px solid #fca5a5; border-radius: 8px; color: #991b1b; font-size: 13px; }
      .ci-warn { margin-top: 10px; padding: 10px 12px; background: #fef3c7; border: 1px solid #fcd34d; border-radius: 8px; color: #78350f; font-size: 12px; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
      .ci-success { margin-top: 10px; padding: 10px 12px; background: #d1fae5; border: 1px solid #a7f3d0; border-radius: 8px; color: #065f46; font-size: 13px; font-weight: 600; }

      /* Sidebar */
      .ci-side { display: flex; flex-direction: column; gap: 12px; }
      .ci-card { background: #fff; border: 1px solid #e5e7eb; border-radius: 10px; padding: 14px; }
      .ci-card h3 { margin: 0 0 8px; font-size: 13px; color: #1a3461; }
      .ci-card ul { margin: 0; padding-left: 18px; font-size: 12px; line-height: 1.6; color: #374151; }
      .ci-card li { margin-bottom: 4px; }
    `}</style>
  )
}
